'use client';
import {
  ErrorPage,
  HeadTags,
  PageAlerts,
  PageSetupScripts,
  pdpSpecificObjects,
} from '@finn/auto-ui/components';
import { trackBreadcrumbClick } from '@finn/auto-ui/components/PageBreadcrumbs/PageBreadcrumbs';
import PartnerVoucherBanner from '@finn/auto-ui/components/PartnerVoucherBanner';
import CoscmicModules from '@finn/auto-ui/components/PDP/CoscmicModules';
import {
  AddToCartContext,
  AddToCartProvider,
} from '@finn/auto-ui/components/ProductDetails/AddToCartProvider';
import PromoBanner from '@finn/auto-ui/components/PromoBanner';
import CheckoutContext from '@finn/auto-ui/contexts/Checkout';
import { useClosedUserGroup } from '@finn/auto-ui/contexts/ClosedUserGroup';
import {
  useCartAvailability,
  useTermAvailability,
  useVehicleData,
} from '@finn/auto-ui/hooks';
import { useTracking } from '@finn/auto-ui/hooks/useTracking';
import { IErrorPage, PDPData, PDPModules } from '@finn/auto-ui/types';
import { CosmicHelperWrapper } from '@finn/platform-modules';
import {
  DiscoveryConfigProvider,
  EmissionModalProvider,
  GenericVehicleDetails,
  getForBusinessCookie,
  ProductDetailsContainer,
  Review,
  useConfigureStore,
  usePreDiscount,
  VehicleProvider,
} from '@finn/ua-vehicle';
import { AppContext } from '@finn/ui-modules';
import { ApiResponse, useUniversalRouter } from '@finn/ui-utils';
import { useContext, useMemo } from 'react';

import Page from '~/layouts/Default';

export type Props = {
  ldMarkup: string;
  pageData: PDPData;
  reviews: Review[];
  ldFAQMarkup?: string;
  pdpModules: PDPModules;
  vehicle: GenericVehicleDetails;
  reviewResponse: ApiResponse<Review>;
  noIndexNoFollow?: boolean;
  canonicalURL?: string;
} & IErrorPage;

const PDPCore = ({ pageData, vehicle, pdpModules, reviews }) => {
  const { onAddToCart } = useContext(AddToCartContext);
  const { triggerDevModal } = useContext(AppContext);
  const { cartInfo } = useContext(CheckoutContext);
  const { closedUserGroupId } = useClosedUserGroup();
  // Allow to render promo in case of there is no patterns provided OR
  // In case any of the patterns is substring of current vehicle id
  const shouldRenderPromoSection = useMemo(() => {
    const supportedIdsPatterns =
      pageData.metadata.content.supported_ids_patterns.patterns;

    return (
      !supportedIdsPatterns.length ||
      supportedIdsPatterns.some((pattern: string) =>
        vehicle.id.includes(pattern)
      )
    );
  }, [pageData, vehicle.id]);

  return (
    <ProductDetailsContainer
      triggerDevModal={triggerDevModal}
      partnerVoucherBanner={<PartnerVoucherBanner className="mb-6" />}
      cosmicModules={
        <CoscmicModules
          vehicle={vehicle}
          pdpModules={pdpModules}
          reviews={reviews}
        />
      }
      renderPromoSection={
        shouldRenderPromoSection
          ? () => (
              <PromoBanner
                pageData={pageData}
                isPdp
                className="mb-4 mt-0 sm:mb-4 sm:min-h-[10px]"
              />
            )
          : undefined
      }
      pageData={pageData}
      closedUserGroupId={closedUserGroupId}
      cartInfo={cartInfo}
      onAddToCart={onAddToCart}
    />
  );
};

export const ProductDetails = ({
  reviews,
  pageData,
  ldMarkup,
  pdpModules,
  statusCode,
  ldFAQMarkup,
  errorMessage,
  reviewResponse,
  vehicle: initialVehicle,
}: Props) => {
  const vehicleId = initialVehicle.id;
  const isForBusiness = getForBusinessCookie();
  const isCartNotAvailable = useCartAvailability();
  const isTermNotAvailable = useTermAvailability();
  const { downPayment } = useConfigureStore();
  const { vehicle, isLoading } = useVehicleData(vehicleId, initialVehicle);
  const preDiscountPrice = usePreDiscount({
    vehicle,
    isForBusiness,
    isDownPayment: downPayment,
  });

  useTracking(vehicle, isLoading, preDiscountPrice);
  const router = useUniversalRouter();

  if (statusCode) {
    return (
      <ErrorPage
        pageData={pageData}
        statusCode={statusCode}
        error={new Error(errorMessage)}
      />
    );
  }

  return (
    <VehicleProvider
      vehicle={vehicle}
      reviews={reviewResponse}
      fearAwayItems={pageData?.metadata?.content?.clickable_fear_away ?? []}
    >
      <EmissionModalProvider
        emissionLabel={pageData?.metadata?.content?.emission_label}
      >
        <CosmicHelperWrapper
          data={pageData}
          preview={false}
          vehicle={vehicle}
          localizationObjects={pdpSpecificObjects}
        />
        <PageSetupScripts ldMarkup={ldMarkup} ldFAQMarkup={ldFAQMarkup} />
        <PageAlerts
          isCartNotAvailable={isCartNotAvailable}
          isTermNotAvailable={isTermNotAvailable}
        />
        <DiscoveryConfigProvider router={router}>
          <Page data={pageData} onBreadcrumbClick={trackBreadcrumbClick}>
            <HeadTags />
            <AddToCartProvider vehicle={vehicle}>
              <PDPCore
                pageData={pageData}
                vehicle={vehicle}
                pdpModules={pdpModules}
                reviews={reviews}
              />
            </AddToCartProvider>
          </Page>
        </DiscoveryConfigProvider>
      </EmissionModalProvider>
    </VehicleProvider>
  );
};
